import React from 'react';
import { ArrowLeft, type LucideIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { SEOHead } from '../SEOHead';

type LegalDocumentSubsection = {
  title: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
};

type LegalDocumentSection = {
  title: string;
  paragraphs?: readonly string[];
  bullets?: readonly string[];
  subsections?: readonly LegalDocumentSubsection[];
};

type LegalDocumentCompanyBlock = {
  title: string;
  intro?: string;
  companyName: string;
  companyDetails: string;
  email: string;
};

type LegalDocumentContent = {
  title: string;
  lastUpdated: string;
  seoTitle: string;
  seoDescription: string;
  backToHomeLabel: string;
  sections: readonly LegalDocumentSection[];
};

type LegalDocumentPageProps = {
  canonicalPath: string;
  content: LegalDocumentContent;
  footerCompanyBlock?: LegalDocumentCompanyBlock;
  icon: LucideIcon;
  keywords: string;
  leadCompanyBlock?: LegalDocumentCompanyBlock;
};

function CompanyBlock({ block }: { block: LegalDocumentCompanyBlock }) {
  return (
    <section>
      <h2 className="mb-4 text-2xl text-gray-900">{block.title}</h2>
      {block.intro ? (
        <p className="mb-4 leading-relaxed text-gray-700">{block.intro}</p>
      ) : null}
      <div className="rounded-lg bg-gray-50 p-4">
        <p className="text-gray-700">
          <strong>{block.companyName}</strong>
          <br />
          {block.companyDetails}
          <br />
          Email: {block.email}
        </p>
      </div>
    </section>
  );
}

export function LegalDocumentPage({
  canonicalPath,
  content,
  footerCompanyBlock,
  icon: Icon,
  keywords,
  leadCompanyBlock,
}: LegalDocumentPageProps) {
  const navigate = useNavigate();

  return (
    <>
      <SEOHead
        overrideSeo={{
          title: content.seoTitle,
          description: content.seoDescription,
          keywords,
          robots: 'noindex, nofollow',
          ogLocale: 'en_US',
        }}
        canonicalPathOverride={canonicalPath}
        canonicalPathIsLocalized={true}
      />
      <Header />
      <main className="min-h-screen vevi-site-background" style={{ paddingTop: '140px' }}>
        <div className="vevi-page-grid">
          <div className="vevi-page-main py-6 sm:py-8 lg:py-10">
            <button
              onClick={() => navigate('/')}
              className="mb-6 flex items-center text-white transition-colors hover:text-purple-200"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              <span>{content.backToHomeLabel}</span>
            </button>

            <div className="mb-8">
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-white/20">
                  <Icon className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl text-white">{content.title}</h1>
                  <p className="text-purple-100">{content.lastUpdated}</p>
                </div>
              </div>
            </div>

            <Card className="bg-white/95 shadow-xl backdrop-blur-sm">
              <CardContent className="space-y-8 p-8">
                {leadCompanyBlock ? <CompanyBlock block={leadCompanyBlock} /> : null}

                {content.sections.map((section) => (
                  <section key={section.title}>
                    <h2 className="mb-4 text-2xl text-gray-900">{section.title}</h2>

                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph} className="mb-3 leading-relaxed text-gray-700 last:mb-0">
                        {paragraph}
                      </p>
                    ))}

                    {section.bullets?.length ? (
                      <ul className="mb-3 ml-4 list-inside list-disc space-y-2 text-gray-700">
                        {section.bullets.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : null}

                    {section.subsections?.map((subsection) => (
                      <div key={subsection.title} className="mt-5">
                        <h3 className="mb-2 text-gray-800">{subsection.title}</h3>
                        {subsection.paragraphs?.map((paragraph) => (
                          <p
                            key={paragraph}
                            className="mb-3 leading-relaxed text-gray-700 last:mb-0"
                          >
                            {paragraph}
                          </p>
                        ))}
                        {subsection.bullets?.length ? (
                          <ul className="ml-4 list-inside list-disc space-y-2 text-gray-700">
                            {subsection.bullets.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    ))}
                  </section>
                ))}

                {footerCompanyBlock ? <CompanyBlock block={footerCompanyBlock} /> : null}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="h-10 sm:h-12 lg:h-14" aria-hidden="true" />

        <Footer />
      </main>
    </>
  );
}
